import { inject, Injectable } from "@angular/core";

import { concatMap, Observable, tap } from "rxjs";

import { EventSourceOptions, ServerSentEventsClient } from "../server-sent-events/sse.client";
import { Streamer } from "../streaming/streamer";
import { AuthService } from "../auth/auth.service";
import { featureFlags } from "@app/environments/environment";

@Injectable({
  providedIn: "root"
})
export class ChatStreamer implements Streamer {
  private authService: AuthService = inject(AuthService);
  private sseClient: ServerSentEventsClient = inject(ServerSentEventsClient);

  private setDefaultOptions(options: EventSourceOptions): EventSourceOptions {
    const defaultOptions = {
      ...options,
      headers: {
        "Content-Type": "application/json",
        ...options.headers
      }
    };

    return defaultOptions;
  }

  stream<T>(url: string, options: EventSourceOptions): Observable<T> {
    options = this.setDefaultOptions(options);

    return this.authService.getAccessToken().pipe(
        tap((accessToken: string) => {
          if (!featureFlags.apiSixLogin) {
            options.headers = {
              ...options.headers,
              Authorization: `Bearer ${accessToken}`
            }
          }
        })
      ).pipe(
        concatMap(() => new Observable<T>((subscriber) => {
          this.sseClient.connect(url, options);
          this.sseClient.onMessage((event: MessageEvent) => {
            try {
              const data: T = JSON.parse(event.data);
              subscriber.next(data);
            } catch (error) {
              this.sseClient.close();
              subscriber.error(error);
            }
          });

          this.sseClient.onClose(() => {
            subscriber.complete();
          });

          this.sseClient.onError((error: unknown) => {
            this.sseClient.close();
            subscriber.error(error);
          });

          return () => this.sseClient.close();
        }))
      );
  }

  disconnect(): void {
    this.sseClient.close();
  }
}
